// Workflow Builder v2 — shared protocol types.
//
// These types are the contract between:
//   1. The edge functions (workflow-builder-discovery / -draft-step-prompt /
//      -analyze-output / -adjust-plan) that emit structured JSON responses.
//   2. The client reducer (workflowSessionReducer) that applies them.
//   3. The UI components that render them.
//
// All edge functions return a JSON object with a top-level `type` discriminator.
// The reducer dispatches on that discriminator.

// ── Lifecycle enums ────────────────────────────────────────────────────

export type WorkflowPhase = 'discovery' | 'executing' | 'finished';

export type StepPhase =
  | 'proposed'
  | 'prompt_drafting'
  | 'prompt_ready'
  | 'awaiting_output'
  | 'analyzing'
  | 'done';

export type StepType = 'manual' | 'ai_assisted' | 'other';

export type WorkflowCadence = 'one_time' | 'recurring' | 'perpetual';

// ── Payload shapes (the body of each typed stream message) ─────────────

export interface PromptDraftPayload {
  version: number;
  title: string;
  body: string;
  tool: string;
  toolNotes?: string;
  /** Approximate token count to help users gauge size before running. */
  approxTokens?: number;
  /** Where the tool prompting guide came from when the drafter ran. Used to
   *  suppress the "Cosmo learned more" regenerate hint when the current draft
   *  already used the researched DB guide. */
  guideSource?: 'db' | 'static' | 'fallback';
}

export interface PastedOutputPayload {
  text: string;
  meta?: {
    bytes?: number;
    rows?: number;
    columns?: number;
    mimeHint?: 'csv' | 'json' | 'markdown' | 'plain';
    filename?: string;
  };
}

export interface AnalysisBullet {
  text: string;
  /** Optional structural hint a renderer can use. */
  severity?: 'info' | 'warn' | 'block';
}

export interface AnalysisDecision {
  text: string;
  options?: string[];
  recommendedOption?: string;
}

/**
 * What Cosmo recommends the user do next from this analysis:
 *   - 'proceed': output is clean and the natural next move is the next step
 *   - 'refine': output needs work in the same step before moving on
 *   - 'decide': real choices exist that block the next step's drafter
 */
export type AnalysisRecommendation = 'proceed' | 'refine' | 'decide';

export interface AnalysisPayload {
  summary: {
    headline: string;
    parsedCleanly: boolean;
    rowsCount?: number;
  };
  sections: {
    looksGood: AnalysisBullet[];
    worthFlagging: AnalysisBullet[];
    decisions: AnalysisDecision[];
  };
  nextStepSetup: {
    ready: boolean;
    /** A paragraph the next step's drafter will receive as carry-over context. */
    contextToCarry: string;
  };
  /** What Cosmo thinks the user should do next. Optional for back-compat with
   *  older analyses generated before this field existed. */
  recommendation?: AnalysisRecommendation;
}

export interface ClarifyChipOption {
  label: string;
  /** Optional value if different from label (e.g. a stable enum the model emits). */
  value?: string;
}

export interface ClarifyPayload {
  question: string;
  reason?: string;
  options: ClarifyChipOption[];
  allowFreeform: boolean;
}

// ── Step-shape used in proposals and plan-diffs (no per-step runtime state) ──

export interface StepDraft {
  id?: string;
  stepName: string;
  description: string;
  type: StepType;
  recommendedTool?: string;
  estimatedDuration?: string;
  inputs: string[];
  outputs: string[];
  handoffInstruction?: string;
}

export interface ProposalPayload {
  name: string;
  objective: string;
  cadence: WorkflowCadence;
  steps: StepDraft[];
  clarifyingQuestions?: ClarifyPayload[];
}

// ── Plan-diff ops (dynamic workflow mutation) ──────────────────────────

export type PlanDiffOp =
  | { op: 'add_step'; position: number; step: StepDraft }
  | { op: 'remove_step'; stepId: string }
  | { op: 'replace_step'; stepId: string; step: StepDraft }
  | { op: 'reorder'; stepIds: string[] }
  | { op: 'update_step'; stepId: string; patch: Partial<StepDraft> };

export interface PlanDiffPayload {
  ops: PlanDiffOp[];
  /** Short natural-language explanation Cosmo emits alongside the diff. */
  chat?: string;
}

// ── Stream messages (what the center pane renders) ────────────────────

interface StreamMessageBase {
  id: string;
  ts: string; // ISO timestamp
  /** Optional step scope; messages without a stepId are workflow-level. */
  stepId?: string;
}

export type StreamMessage =
  | (StreamMessageBase & { kind: 'chat'; role: 'user' | 'assistant'; content: string })
  | (StreamMessageBase & { kind: 'prompt_artifact'; stepId: string; payload: PromptDraftPayload })
  | (StreamMessageBase & { kind: 'pasted_output'; stepId: string; payload: PastedOutputPayload })
  | (StreamMessageBase & { kind: 'analysis'; stepId: string; payload: AnalysisPayload })
  | (StreamMessageBase & { kind: 'clarify'; payload: ClarifyPayload })
  | (StreamMessageBase & { kind: 'proposal'; payload: ProposalPayload })
  | (StreamMessageBase & { kind: 'system'; text: string })
  | (StreamMessageBase & { kind: 'task_redirect' });

// ── Edge function response envelopes ──────────────────────────────────

export type DiscoveryResponse =
  | { type: 'proposal'; proposal: ProposalPayload; chat?: string }
  | { type: 'clarify'; clarify: ClarifyPayload }
  | { type: 'task_redirect'; message: string };

export type DraftStepPromptResponse =
  | { type: 'prompt_draft'; draft: PromptDraftPayload }
  | { type: 'clarify'; clarify: ClarifyPayload };

export type AnalyzeOutputResponse = { type: 'analysis'; analysis: AnalysisPayload };

export type AdjustPlanResponse =
  | { type: 'plan_diff'; diff: PlanDiffPayload }
  | { type: 'chat'; content: string };

// ── Step runtime state (one per workflow_steps row, in client memory) ──

export interface DraftErrorState {
  kind: 'timeout' | 'network' | 'rate_limit' | 'usage_limit' | 'server_error' | 'unknown';
  message: string;
  retryable: boolean;
}

export interface StepState {
  id: string;
  position: number;
  stepName: string;
  description: string;
  type: StepType;
  recommendedTool?: string;
  estimatedDuration?: string;
  inputs: string[];
  outputs: string[];
  handoffInstruction?: string;
  phase: StepPhase;
  promptDraft?: PromptDraftPayload;
  promptHistory: PromptDraftPayload[];
  pastedOutput?: PastedOutputPayload;
  analysis?: AnalysisPayload;
  decisions: { question: string; chosen: string }[];
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  /** Linked fluency score/submission IDs — set when the prompt is scored. */
  scoreId?: string;
  submissionId?: string;
  /** Transient — not persisted. Set when a drafting/analysis call fails. */
  draftError?: DraftErrorState;
}

// ── Top-level workflow session ────────────────────────────────────────

export interface WorkflowSession {
  id: string;
  userId: string;
  orgId: string | null;
  name: string | null;
  objective: string | null;
  cadence: WorkflowCadence | null;
  phase: WorkflowPhase;
  activeStepId: string | null;
  selectedTool: string | null;
  steps: StepState[];
  messages: StreamMessage[];
  startedAt: string;
  completedAt: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────

export function generateId(): string {
  return crypto.randomUUID();
}

export function nowIso(): string {
  return new Date().toISOString();
}

/** Sort steps by position; stable. */
export function sortStepsByPosition(steps: StepState[]): StepState[] {
  return [...steps].sort((a, b) => a.position - b.position);
}

/** Find a step by id; returns undefined if not present. */
export function findStep(steps: StepState[], stepId: string): StepState | undefined {
  return steps.find(s => s.id === stepId);
}

/** Find the next step after the given step id by position. */
export function nextStep(steps: StepState[], stepId: string): StepState | undefined {
  const sorted = sortStepsByPosition(steps);
  const idx = sorted.findIndex(s => s.id === stepId);
  if (idx === -1 || idx === sorted.length - 1) return undefined;
  return sorted[idx + 1];
}
